import { computed } from "vue";
import type { ComputedRef } from "vue";
import { useContext } from "#imports";
import type { Schemas, operations } from "#shopware";

/**
 * @internal
 */
export type Breadcrumb =
  | {
      name: string;
      path?: string;
    }
  | Schemas["Breadcrumb"];

/**
 * @public
 */
export type UseBreadcrumbsReturn = {
  /**
   * Clear breadcrumbs store
   */
  clearBreadcrumbs(): void;
  /**
   * List of breadcrumbs
   */
  breadcrumbs: ComputedRef<Breadcrumb[]>;
  /**
   * Build breadcrumbs dynamically for a category by fetching them from the API
   *
   * @param {operations["readBreadcrumb get /breadcrumb/{id}"]["response"]} breadcrumbs
   */
  buildDynamicBreadcrumbs(
    breadcrumbs: operations["readBreadcrumb get /breadcrumb/{id}"]["response"],
  ): Promise<void>;
  /**
   * Add a breadcrumb to the breadcrumbs list
   *
   * @param {Breadcrumb} breadcrumb
   */
  pushBreadcrumb(breadcrumb: Breadcrumb): void;
};

/**
 * Composable for breadcrumbs management.
 * Read the [guide](https://frontends.shopware.com/getting-started/page-elements/breadcrumbs.html#building-breadcrumbs-for-cms-pages).
 *
 * @public
 * @category CMS (Shopping Experiences)
 */
export function useBreadcrumbs(
  newBreadcrumbs?: Breadcrumb[],
): UseBreadcrumbsReturn {
  // Store for breadcrumbs
  const _breadcrumbs = useContext<Breadcrumb[]>("swBreadcrumb", {
    replace: newBreadcrumbs,
  });

  /**
   * Clear breadcrumbs store
   */
  const clearBreadcrumbs = () => {
    _breadcrumbs.value = [];
  };

  const pushBreadcrumb = (breadcrumb: Breadcrumb) => {
    if (_breadcrumbs.value) _breadcrumbs.value.push(breadcrumb);
    else _breadcrumbs.value = [breadcrumb];
  };

  const buildDynamicBreadcrumbs = async (
    breadcrumbs: operations["readBreadcrumb get /breadcrumb/{id}"]["response"],
  ) => {
    let breadcrumbsData  = breadcrumbs?.breadcrumbs || breadcrumbs;
    _breadcrumbs.value = breadcrumbsData.map((breadcrumb) => {
      // Adjust path to be compatible with the router
      return {
        ...breadcrumb,
        path: `/${breadcrumb.path}`,
      };
    });
  };

  return {
    clearBreadcrumbs,
    breadcrumbs: computed(() => _breadcrumbs.value),
    buildDynamicBreadcrumbs,
    pushBreadcrumb,
  };
}

type BreadcrumbListItem = {
  name: string;
  path?: string;
};

export function useBreadcrumbJsonLD() {
  const { t, locale, defaultLocale } = useI18n();
  const config = useRuntimeConfig();

  const buildAbsoluteUrl = (path: string) => {
    const baseUrl =
      config.public.shopware?.devStorefrontUrl ||
      (config.shopware as { devStorefrontUrl?: string })?.devStorefrontUrl ||
      "";
    const needsLocalePrefix = locale.value !== defaultLocale;
    const localePrefix = needsLocalePrefix ? `/${locale.value}` : "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${baseUrl}${localePrefix}${normalizedPath}`;
  };

  const buildListItems = (
    trail: BreadcrumbListItem[] = [],
    options: { includeHome?: boolean } = {},
  ) => {
    const { includeHome = false } = options;
    const items: Array<Record<string, string | number>> = [];

    if (includeHome) {
      items.push({
        "@type": "ListItem",
        position: 1,
        name: t("home"),
        item: buildAbsoluteUrl("/"),
      });
    }

    trail.forEach((crumb, index) => {
      items.push({
        "@type": "ListItem",
        position: includeHome ? index + 2 : index + 1,
        name: crumb.name,
        item: buildAbsoluteUrl(crumb.path || "/"),
      });
    });

    return items;
  };

  return { buildListItems, buildAbsoluteUrl };
}
